import { action, computed, observable } from 'mobx';
import { fromStream, IStreamListener } from 'mobx-utils';
import StreemSDK, {
    RoomSession,
    UserSession,
    GroupReservation,
    UserAvailabilityStatus,
} from '@streem/sdk-core';
import { WallItemSubtype } from '@streem/sdk-types';
import { streem } from 'streem-sdk-protobuf';
import { APITypes } from '@streem/api';
import { of } from 'rxjs';
import { processCallLogDetails } from '../util/remote_user_call_log';
import appLogger from '../util/logging/app_logger';
import {
    addCallSourceToClarity,
    addReservationIdtoClaritySession,
    addRoomIdToClaritySession,
    upgradeClaritySession,
} from '../util/clarity';
const GroupReservationStatus = streem.api.GroupReservationStatus;

export class SDKStore {
    public readonly configured = fromStream(StreemSDK.configured, false);
    public readonly userSession = fromStream<UserSession | undefined>(StreemSDK.userSession);
    private userWallReconnectFailedYnStream: IStreamListener<boolean>;
    private roomSessionStream?: IStreamListener<RoomSession | undefined>;
    private userAvailabilityStatusStream?: IStreamListener<UserAvailabilityStatus | undefined>;
    private groupReservationStream?: IStreamListener<GroupReservation | undefined>;
    private smsDeliveryStatusStream?: IStreamListener<
        WallItemSubtype<streem.user.UserWallItem, 'smsDeliveryStatus'> | undefined
    >;

    // Once the user session initiates a room session, this will return that room session
    @computed
    public get roomSession(): IStreamListener<RoomSession | undefined> | undefined {
        if (this.roomSessionStream) {
            appLogger.info(
                `Getting roomSessionStream::roomId=${this.roomSessionStream.current?.roomId}`,
            );
            this.roomSessionStream.dispose();
            this.roomSessionStream = undefined;
        }

        if (this.userSession.current) {
            this.roomSessionStream = fromStream(this.userSession.current.roomSession);
            appLogger.info(
                `Getting from userSession roomSessionStream::roomId=${this.roomSessionStream?.current?.roomId}`,
            );
            if (this.roomSessionStream?.current?.roomId) {
                addRoomIdToClaritySession(`${this.roomSessionStream?.current?.roomId}`);
            }

            return this.roomSessionStream;
        }

        return undefined;
    }

    @action
    public async getRoomCallLogHistory() {
        if (this.roomSession) {
            const calls = await this.roomSession?.current?.hostUser?.getHostUserCallLog();
            return processCallLogDetails(calls);
        }
    }

    @computed
    public get userAvailabilityStatus():
        | IStreamListener<UserAvailabilityStatus | undefined>
        | undefined {
        if (this.userAvailabilityStatusStream) {
            this.userAvailabilityStatusStream.dispose();
            this.userAvailabilityStatusStream = undefined;
        }

        if (this.userSession.current) {
            this.userAvailabilityStatusStream = fromStream(
                this.userSession.current.currentAvailabilityStatus,
            );
            return this.userAvailabilityStatusStream;
        }

        return undefined;
    }

    @computed
    public get groupReservation(): IStreamListener<GroupReservation | undefined> | undefined {
        if (this.groupReservationStream) {
            this.groupReservationStream.dispose();
            this.groupReservationStream = undefined;
        }

        if (this.userSession.current) {
            this.groupReservationStream = fromStream(this.userSession.current.groupReservations);

            if (
                this.groupReservationStream?.current?.reservationStatus ===
                GroupReservationStatus.GROUP_RESERVATION_STATUS_CONFIRMING
            ) {
                upgradeClaritySession();
                addReservationIdtoClaritySession(
                    this.groupReservationStream.current.reservationSid,
                );
            }

            if (this.groupReservationStream?.current?.callDetail?.callSourceCode) {
                addCallSourceToClarity(
                    this.groupReservationStream.current.callDetail.callSourceCode,
                );
            }
            return this.groupReservationStream;
        }

        return undefined;
    }

    @observable
    public getSmsInviteStatusBySid(
        invitationSid: string,
    ):
        | IStreamListener<
              WallItemSubtype<streem.user.UserWallItem, 'smsDeliveryStatus'> | undefined
          >
        | undefined {
        if (this.smsDeliveryStatusStream) {
            this.smsDeliveryStatusStream.dispose();
            this.smsDeliveryStatusStream = undefined;
        }

        if (this.userSession.current) {
            this.smsDeliveryStatusStream = fromStream(
                this.userSession.current.getSmsInviteStatusBySid(invitationSid),
            );
            return this.smsDeliveryStatusStream;
        }

        return undefined;
    }

    @observable
    private _groupReservationCallSource?: APITypes.StreemApiCallSource | undefined;

    @computed
    public get groupReservationCallSource(): APITypes.StreemApiCallSource | undefined {
        return this._groupReservationCallSource;
    }

    @action
    public setGroupReservationCallSource(companyCallSource: Array<APITypes.StreemApiCallSource>) {
        const currentGroupReservation = this.groupReservation.current;
        if (companyCallSource && currentGroupReservation && currentGroupReservation.callDetail) {
            this._groupReservationCallSource = companyCallSource.find(
                callSource => callSource.sid === currentGroupReservation?.callDetail?.callSourceSid,
            );

            if (this._groupReservationCallSource == null) {
                appLogger.warn(
                    `Call source not found for callSourceId: ${this.groupReservation.current?.callDetail?.callSourceSid} for reservation: ${this.groupReservation.current?.id}`,
                );
            }
        }
    }

    @action
    public clearGroupReservationCallSource() {
        this._groupReservationCallSource = undefined;
    }

    @computed
    public get userWallSyncReconnectFailed(): IStreamListener<boolean> {
        if (this.userWallReconnectFailedYnStream) {
            this.userWallReconnectFailedYnStream.dispose();
            this.userWallReconnectFailedYnStream = undefined;
        }

        if (this.userSession.current) {
            this.userWallReconnectFailedYnStream = fromStream(
                this.userSession.current.userWallReconnectFailed,
            );
            return this.userWallReconnectFailedYnStream;
        }

        // Default to false to match initial value of reconnectFailed from twilio_wall_sync
        return fromStream(of(false));
    }
}
